import { useState, useEffect, useRef } from 'react';
import { fetchEVMakes, fetchEVModels, fetchEVVariants } from '../../../../utils/evProfiles';

const useEVData = (oAuth, apiKey, initialProfile = null) => {
  const [makes, setMakes] = useState([]);
  const [models, setModels] = useState([]);
  const [variants, setVariants] = useState([]);

  const [selectedMake, setSelectedMake] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [selectedVariant, setSelectedVariant] = useState('');
  const [selectedVariantData, setSelectedVariantData] = useState(null);

  const hasInitialized = useRef(false);
  const prevMakeRef = useRef('');
  const prevModelRef = useRef('');

  useEffect(() => {
    const hasAuth = apiKey || (oAuth && oAuth.accessToken);
    if (!hasAuth) {
      return;
    }

    const loadMakes = async () => {
      const makesData = await fetchEVMakes(oAuth, apiKey);
      setMakes(makesData);

      if (initialProfile?.make && !hasInitialized.current) {
        const makeMatch = makesData.find((m) => m.name === initialProfile.make);
        if (makeMatch) {
          setSelectedMake(makeMatch.id);
          prevMakeRef.current = makeMatch.id;
        }
      }
    };
    loadMakes();
  }, [oAuth, apiKey]);

  useEffect(() => {
    if (!selectedMake) {
      setModels([]);
      setSelectedModel('');
      setSelectedVariant('');
      setVariants([]);
      setSelectedVariantData(null);
      prevMakeRef.current = '';
      prevModelRef.current = '';
      return;
    }

    const makeChanged = prevMakeRef.current && prevMakeRef.current !== selectedMake;
    if (makeChanged) {
      setSelectedModel('');
      setSelectedVariant('');
      setVariants([]);
      setSelectedVariantData(null);
      prevModelRef.current = '';
    }
    prevMakeRef.current = selectedMake;

    const hasAuth = apiKey || (oAuth && oAuth.accessToken);
    if (!hasAuth) {
      return;
    }

    const loadModels = async () => {
      const modelsData = await fetchEVModels(selectedMake, oAuth, apiKey);
      setModels(modelsData);

      if (initialProfile?.model && !hasInitialized.current) {
        const modelMatch = modelsData.find((m) => m.name === initialProfile.model);
        if (modelMatch) {
          setSelectedModel(modelMatch.id);
          prevModelRef.current = modelMatch.id;
        }
      }
    };
    loadModels();
  }, [selectedMake, oAuth, apiKey]);

  useEffect(() => {
    if (!selectedMake || !selectedModel) {
      setVariants([]);
      setSelectedVariant('');
      setSelectedVariantData(null);
      prevModelRef.current = selectedMake ? prevModelRef.current : '';
      return;
    }

    const modelChanged = prevModelRef.current && prevModelRef.current !== selectedModel;
    if (modelChanged) {
      setSelectedVariant('');
      setSelectedVariantData(null);
    }
    prevModelRef.current = selectedModel;

    const hasAuth = apiKey || (oAuth && oAuth.accessToken);
    if (!hasAuth) {
      return;
    }

    const loadVariants = async () => {
      const variantsData = await fetchEVVariants(selectedMake, selectedModel, oAuth, apiKey);
      setVariants(variantsData);

      if (initialProfile?.profileId && !hasInitialized.current) {
        const variantMatch = variantsData.find((v) => v.profileId === initialProfile.profileId);
        if (variantMatch) {
          setSelectedVariant(variantMatch.profileId);
        }
        hasInitialized.current = true;
      }
    };
    loadVariants();
  }, [selectedMake, selectedModel, oAuth, apiKey]);

  useEffect(() => {
    if (selectedVariant && variants.length > 0) {
      const variantData = variants.find((v) => v.profileId === selectedVariant);
      setSelectedVariantData(variantData || null);
    } else {
      setSelectedVariantData(null);
    }
  }, [selectedVariant, variants]);

  return {
    makes,
    models,
    variants,
    selectedMake,
    selectedModel,
    selectedVariant,
    setSelectedMake,
    setSelectedModel,
    setSelectedVariant,
    selectedVariantData,
  };
};

export default useEVData;
